"use client"

import { useEffect } from "react"
import { useRouter } from "next/navigation"
import { AnimatePresence, motion } from "framer-motion"
import { useApp } from "@/lib/context"

/**
 * Notificação viva em glassmorfismo — aparece na base do ecrã.
 * Imagem do primeiro prato à esquerda, mensagem + "Ver mais" à direita
 * que abre a página Agora, onde o pedido pode ser acompanhado em tempo real.
 */
export default function LiveToast() {
  const router = useRouter()
  const {
    liveNotification,
    limparNotificacaoViva,
    estabelecimento,
    setCurrentOrderId,
  } = useApp()

  // Auto-desmiss após alguns segundos
  useEffect(() => {
    if (!liveNotification) return
    const timer = setTimeout(() => limparNotificacaoViva(), 5000)
    return () => clearTimeout(timer)
  }, [liveNotification, limparNotificacaoViva])

  const verAgora = () => {
    if (!liveNotification) return
    setCurrentOrderId(liveNotification.pedidoId)
    limparNotificacaoViva()
    router.push("/agora")
  }

  const logo = estabelecimento?.logo || "/placeholder-logo.png"

  return (
    <AnimatePresence>
      {liveNotification && (
        <motion.div
          key={`${liveNotification.pedidoId}-${liveNotification.mensagem}`}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 260 }}
          className="fixed inset-x-0 bottom-4 z-[95] flex justify-center px-4 pb-[env(safe-area-inset-bottom)]"
          role="status"
          aria-live="polite"
        >
          <div className="glass-toast-qr flex w-full max-w-sm items-center gap-3 rounded-2xl p-2 pr-3 shadow-2xl">
            {/* Imagem do prato ou logotipo */}
            <div className="relative size-14 shrink-0 overflow-hidden rounded-xl bg-white/10">
              <img
                src={liveNotification.imagem || logo}
                alt=""
                aria-hidden="true"
                className="size-full object-cover"
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).src = logo
                }}
              />
            </div>

            {/* Mensagem */}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground leading-tight">
                {liveNotification.mensagem}
              </p>
              <p className="mt-0.5 text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                Pedido #{liveNotification.pedidoId.slice(-6)}
              </p>
            </div>

            {/* Ação Ver mais */}
            <button
              onClick={verAgora}
              className="shrink-0 rounded-full bg-orange-500 px-3 py-1.5 text-xs font-bold text-white shadow-md transition-transform hover:scale-[1.04] active:scale-95"
            >
              Ver mais
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
